import { useEffect, useMemo, useState } from 'react';
import { CheckCard, CheckHistogram } from '@/components/check';
import { API_BASE_URL } from '@/lib/api';
import { track } from '@/lib/analytics';
import {
  RATE_QUIZZES,
  type CheckCard as CheckCardData,
  type RateSlug,
} from '@/lib/check/quizzes';
import { harborHomeUrl, harborSatelliteUrl, requireSatellite } from './config';
import { currentSatellitePath } from './detect';
import {
  fetchNextPair,
  fetchSubmission,
  patchRateSession,
  submitScenario,
  votePair,
} from './rate/api';
import { RateLink, RateShell, nunito } from './rate/shell';
import type { RatePair, RateSessionState, RateSubmissionStats, RateVoteResult } from './rate/types';

const QUIZ_BY_APEX: Record<string, RateSlug> = {
  'ratemycoparent.com': 'rate_other',
  'ratemycoparenting.com': 'rate_self',
};

const CROSS_LINK: Record<string, { href: string; label: string }> = {
  'ratemycoparent.com': {
    href: 'https://ratemycoparenting.com/',
    label: 'Rate how you show up',
  },
  'ratemycoparenting.com': {
    href: 'https://ratemycoparent.com/',
    label: 'Rate the other coparent',
  },
};

const HOOK: Record<string, { kicker: string; headline: string; sub: string }> = {
  'ratemycoparent.com': {
    kicker: 'Rate My Coparent',
    headline: 'Which situation is worse?',
    sub: 'two at a time. pick the one that’s worse.',
  },
  'ratemycoparenting.com': {
    kicker: 'Rate My Coparenting',
    headline: 'Which parent handled this better?',
    sub: 'two at a time. pick how you would rate it.',
  },
};

const DROP_CATEGORIES = [
  { key: 'texts', label: 'Texts' },
  { key: 'pickups', label: 'Pickups' },
  { key: 'money', label: 'Money' },
  { key: 'plans', label: 'Plans' },
];

const INTAKE = [
  {
    key: 'situation',
    prompt: 'Your situation',
    options: [
      { key: 'divorced', label: 'Divorced' },
      { key: 'never_married', label: 'Never married' },
      { key: 'separated', label: 'Separated' },
      { key: 'step', label: 'Step / blended' },
    ],
  },
  {
    key: 'custody',
    prompt: 'Custody is mostly',
    options: [
      { key: 'fifty_fifty', label: 'About 50/50' },
      { key: 'every_other_weekend', label: 'Every other weekend' },
      { key: 'primary', label: 'Mostly with one of us' },
      { key: 'week_on', label: 'Week on / week off' },
      { key: 'figuring', label: 'Still figuring it out' },
    ],
  },
];

function useSatellitePath(): string {
  const [path, setPath] = useState(currentSatellitePath);
  useEffect(() => {
    const onPop = () => setPath(currentSatellitePath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return path;
}

function go(to: string) {
  window.history.pushState({}, '', `${to}${window.location.search}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo(0, 0);
}

function parsePairId(path: string): string | null {
  const match = path.match(/^\/v\/([0-9a-fA-F-]{8,64}--[0-9a-fA-F-]{8,64})$/);
  return match?.[1] ?? null;
}

function parseResultId(path: string): string | null {
  const match = path.match(/^\/r\/([0-9a-fA-F-]{8,64})$/);
  return match?.[1] ?? null;
}

function parseSubmissionId(path: string): string | null {
  const match = path.match(/^\/s\/([0-9a-fA-F-]{8,64})$/);
  return match?.[1] ?? null;
}

export function RateQuizPage({ apex }: { apex: string }) {
  const config = requireSatellite(apex);
  const quizSlug = QUIZ_BY_APEX[apex];
  const quiz = RATE_QUIZZES[quizSlug];
  const path = useSatellitePath();
  const harborTool = harborSatelliteUrl(config);
  const harborHome = harborHomeUrl(config.apex);
  const cross = CROSS_LINK[apex];
  const pairId = parsePairId(path);
  const publicId = parseResultId(path);
  const submissionId = parseSubmissionId(path);

  if (!quiz) {
    return <p className='p-6'>Unknown quiz.</p>;
  }

  const shared = {
    apex,
    configName: config.name,
    title: config.title,
    description: config.description,
    harborHome,
    cross,
  };

  if (publicId) {
    return (
      <RateResult
        {...shared}
        quizSlug={quizSlug}
        publicId={publicId}
        harborTool={harborTool}
        harborCta={config.harborCta}
      />
    );
  }
  if (submissionId) {
    return <RateStats {...shared} submissionId={submissionId} />;
  }
  if (path === '/drop') {
    return <RateDrop {...shared} />;
  }
  if (path === '/unlock') {
    return <RateUnlock {...shared} tagline={config.tagline} quizSlug={quizSlug} faqs={config.faqs} />;
  }
  return <RateVoteBoard {...shared} tagline={config.tagline} featuredPair={pairId} faqs={config.faqs} />;
}

function RateVoteBoard({
  apex,
  configName,
  title,
  description,
  tagline,
  harborHome,
  cross,
  featuredPair,
  faqs,
}: {
  apex: string;
  configName: string;
  title: string;
  description: string;
  tagline: string;
  harborHome: string;
  featuredPair: string | null;
  faqs: { question: string; answer: string }[];
  cross?: { href: string; label: string };
}) {
  const hook = HOOK[apex];
  const [state, setState] = useState<RateSessionState | null>(null);
  const [split, setSplit] = useState<RateVoteResult | null>(null);
  const [lastPair, setLastPair] = useState<RatePair | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async (opts?: { pair?: string }) => {
    setError(null);
    try {
      const next = await fetchNextPair(apex, {
        pair: opts?.pair,
      });
      setState(next);
      setSplit(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load a pair.');
    }
  };

  useEffect(() => {
    void load({ pair: featuredPair || undefined });
    // featured pair only on first mount / path change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apex, featuredPair]);

  useEffect(() => {
    if (!state?.pair) return;
    track('Rate Pair View', {
      host: apex,
      pair: state.pair.id,
      category: state.pair.category,
      vote_count: state.vote_count,
    });
  }, [apex, state?.pair?.id, state?.pair?.category, state?.vote_count]);

  const onVote = async (winnerId: string) => {
    if (!state?.pair || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await votePair(apex, {
        left_id: state.pair.left.id,
        right_id: state.pair.right.id,
        winner_id: winnerId,
      });
      track('Rate Pair Voted', {
        host: apex,
        pair: state.pair.id,
        winner: winnerId,
        vote_count: result.vote_count,
      });
      setLastPair(state.pair);
      setSplit(result);
      setState(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that vote.');
    } finally {
      setBusy(false);
    }
  };

  const remaining = state
    ? Math.max(0, state.votes_to_unlock - state.vote_count)
    : 8;

  return (
    <RateShell
      apex={apex}
      name={configName}
      title={title}
      description={description}
      path={featuredPair ? `/v/${featuredPair}` : '/'}
      harborHome={harborHome}
      cross={cross}
      ogImage={
        featuredPair
          ? `${API_BASE_URL || `https://${apex}`}/v1/rate/pairs/${featuredPair}/og.png?host=${apex}`
          : undefined
      }
      noindex={Boolean(featuredPair)}
    >
      <p className='text-xs font-semibold uppercase tracking-wide text-primary' style={nunito}>
        {hook?.kicker || configName}
      </p>
      <h1 className='mt-2 text-3xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
        {state?.pair?.prompt || hook?.headline || configName}
      </h1>
      <p className='mt-2 text-gray-500' style={nunito}>
        {hook?.sub || tagline}
      </p>

      {split && lastPair ? (
        <div className='mt-6 space-y-3'>
          <p className='text-sm font-semibold text-gray-500' style={nunito}>
            {split.pair_votes} vote{split.pair_votes === 1 ? '' : 's'} on this pair
          </p>
          {[lastPair.left, lastPair.right].map((card, idx) => {
            const pct = idx === 0 ? split.left_pct : split.right_pct;
            const won = card.id === split.winner_id;
            return (
              <div
                key={card.id}
                className={`rounded-2xl px-4 py-4 ${
                  won ? 'bg-primary text-white' : 'bg-gray-100 text-gray-800'
                }`}
              >
                <p className='text-sm font-bold' style={nunito}>
                  {pct}% {won ? 'picked this' : ''}
                </p>
                <p className='mt-1 text-base font-semibold' style={nunito}>
                  {card.body}
                </p>
              </div>
            );
          })}
          {split.unlocked ? (
            <button
              type='button'
              onClick={() => {
                track('Rate Report Unlocked', { host: apex, vote_count: split.vote_count });
                go('/unlock');
              }}
              className='w-full rounded-2xl bg-primary px-6 py-4 text-lg font-semibold text-white'
              style={nunito}
            >
              Unlock your report →
            </button>
          ) : (
            <button
              type='button'
              onClick={() => void load()}
              className='w-full rounded-2xl bg-primary px-6 py-4 text-lg font-semibold text-white'
              style={nunito}
            >
              Next pair →
            </button>
          )}
          {split.unlocked ? (
            <button
              type='button'
              onClick={() => void load()}
              className='w-full text-sm font-semibold text-primary'
              style={nunito}
            >
              Keep voting
            </button>
          ) : (
            <p className='text-center text-sm text-gray-500' style={nunito}>
              {remaining} more to unlock your report
            </p>
          )}
        </div>
      ) : state?.pair ? (
        <div className='mt-6 grid gap-3'>
          {[state.pair.left, state.pair.right].map((card) => (
            <button
              key={card.id}
              type='button'
              disabled={busy}
              onClick={() => void onVote(card.id)}
              className='rounded-2xl bg-gray-100 px-4 py-5 text-left text-lg font-semibold text-gray-800 transition-colors hover:bg-primary hover:text-white disabled:opacity-60'
              style={nunito}
            >
              {card.body}
            </button>
          ))}
          <p className='pt-2 text-center text-sm text-gray-500' style={nunito}>
            {state.unlocked
              ? 'Report unlocked. Keep going or open it when you want.'
              : `${remaining} more to unlock your report`}
          </p>
          {state.unlocked ? (
            <RateLink
              to='/unlock'
              onClick={() =>
                track('Rate Report Unlocked', { host: apex, vote_count: state.vote_count })
              }
              className='text-center text-sm font-semibold text-primary'
              style={nunito}
            >
              See your report →
            </RateLink>
          ) : null}
        </div>
      ) : (
        <p className='mt-6 text-gray-500' style={nunito}>
          {state?.exhausted
            ? 'You have seen every pair we have right now. Unlock your report or drop a text.'
            : 'Loading a pair…'}
        </p>
      )}

      {error ? (
        <p className='mt-4 text-sm text-red-600' style={nunito}>
          {error}
        </p>
      ) : null}

      <div className='mt-8 grid gap-2 text-center text-sm' style={nunito}>
        <RateLink to='/drop' className='font-semibold text-primary'>
          Drop a text
        </RateLink>
      </div>

      <section className='mt-10'>
        <h2 className='text-lg font-semibold'>Questions</h2>
        <dl className='mt-4 space-y-5 text-sm'>
          {faqs.map((faq) => (
            <div key={faq.question}>
              <dt className='font-medium'>{faq.question}</dt>
              <dd className='mt-1 text-gray-600'>{faq.answer}</dd>
            </div>
          ))}
        </dl>
      </section>
    </RateShell>
  );
}

function RateUnlock({
  apex,
  configName,
  title,
  description,
  tagline,
  quizSlug,
  harborHome,
  faqs,
  cross,
}: {
  apex: string;
  configName: string;
  title: string;
  description: string;
  tagline: string;
  quizSlug: RateSlug;
  harborHome: string;
  faqs: { question: string; answer: string }[];
  cross?: { href: string; label: string };
}) {
  const quiz = RATE_QUIZZES[quizSlug];
  const questions = useMemo(() => {
    const role = quiz.questions[0];
    const rest = quiz.questions.slice(1);
    return [role, ...INTAKE, ...rest];
  }, [quiz.questions]);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const question = questions[step];
  const total = questions.length;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchNextPair(apex);
        if (!cancelled) {
          if (!next.unlocked) {
            go('/');
            return;
          }
          setAllowed(true);
        }
      } catch {
        if (!cancelled) setAllowed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apex]);

  useEffect(() => {
    if (!allowed) return;
    track('Rate Quiz Screen', { host: apex, quiz: quizSlug, step: question.key });
  }, [allowed, apex, quizSlug, question.key]);

  const submit = async (nextAnswers: Record<string, string>) => {
    setSubmitting(true);
    setError(null);
    try {
      await patchRateSession(apex, {
        role: nextAnswers.role,
        situation: nextAnswers.situation,
        custody: nextAnswers.custody,
      });
      const checkAnswers = Object.fromEntries(
        Object.entries(nextAnswers).filter(
          ([key]) => key !== 'situation' && key !== 'custody',
        ),
      );
      const res = await fetch(`${API_BASE_URL}/v1/checks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quiz_slug: quizSlug, answers: checkAnswers }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { detail?: string };
        throw new Error(body.detail || 'Could not save your check.');
      }
      const data = (await res.json()) as { public_id: string };
      await patchRateSession(apex, { report_public_id: data.public_id });
      track('Rate Report Unlocked', { host: apex, quiz: quizSlug, public_id: data.public_id });
      go(`/r/${data.public_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your check.');
      setSubmitting(false);
    }
  };

  const onPick = (key: string) => {
    const next = { ...answers, [question.key]: key };
    setAnswers(next);
    track('Rate Question Answered', {
      host: apex,
      quiz: quizSlug,
      question: question.key,
      answer: key,
    });
    if (step + 1 < total) {
      setStep(step + 1);
      return;
    }
    void submit(next);
  };

  if (!allowed) {
    return (
      <RateShell
        apex={apex}
        name={configName}
        title={title}
        description={description}
        path='/unlock'
        harborHome={harborHome}
        cross={cross}
        noindex
      >
        <p className='text-gray-500' style={nunito}>
          Loading…
        </p>
      </RateShell>
    );
  }

  return (
    <RateShell
      apex={apex}
      name={configName}
      title={title}
      description={description}
      path='/unlock'
      harborHome={harborHome}
      cross={cross}
      noindex
    >
      <p className='text-xs font-semibold uppercase tracking-wide text-primary' style={nunito}>
        {step < 3 ? 'First, a bit about you' : configName}
      </p>
      <p className='mt-2 text-sm text-gray-500' style={nunito}>
        {step + 1} / {total}
      </p>
      <h1 className='mt-2 text-3xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
        {step === 0 ? 'First, a bit about you' : question.prompt}
      </h1>
      {step === 0 ? (
        <p className='mt-2 text-gray-600' style={nunito}>
          asked once, then we make your card. {tagline}
        </p>
      ) : (
        <button
          type='button'
          className='mt-4 text-sm font-semibold text-primary'
          style={nunito}
          onClick={() => setStep(step - 1)}
        >
          Back
        </button>
      )}
      {step === 0 ? (
        <p className='mt-6 text-lg font-semibold text-gray-900' style={nunito}>
          {question.prompt}
        </p>
      ) : null}
      <div className='mt-4 grid gap-3'>
        {question.options.map((opt) => {
          const selected = answers[question.key] === opt.key;
          return (
            <button
              key={opt.key}
              type='button'
              disabled={submitting}
              onClick={() => onPick(opt.key)}
              className={`rounded-2xl px-4 py-4 text-left text-lg font-semibold transition-colors ${
                selected ? 'bg-primary text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
              style={nunito}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {error ? (
        <p className='mt-4 text-sm text-red-600' style={nunito}>
          {error}
        </p>
      ) : null}
      <section className='mt-12'>
        <h2 className='text-xl font-semibold'>Questions</h2>
        <dl className='mt-4 space-y-5'>
          {faqs.map((faq) => (
            <div key={faq.question}>
              <dt className='font-medium'>{faq.question}</dt>
              <dd className='mt-1 text-gray-600'>{faq.answer}</dd>
            </div>
          ))}
        </dl>
      </section>
    </RateShell>
  );
}

function RateResult({
  apex,
  configName,
  title,
  description,
  quizSlug,
  publicId,
  harborTool,
  harborHome,
  harborCta,
  cross,
}: {
  apex: string;
  configName: string;
  title: string;
  description: string;
  quizSlug: RateSlug;
  publicId: string;
  harborTool: string;
  harborHome: string;
  harborCta: string;
  cross?: { href: string; label: string };
}) {
  const [card, setCard] = useState<CheckCardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shareLabel, setShareLabel] = useState('Share your result');
  const shareUrl = `https://${apex}/r/${publicId}`;
  const ogImage = `${API_BASE_URL || `https://${apex}`}/v1/checks/${publicId}/og.png`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/v1/checks/${publicId}`);
        if (!res.ok) throw new Error('This result is gone.');
        const data = (await res.json()) as { card: CheckCardData };
        if (data.card.quiz_slug !== quizSlug) {
          throw new Error('This result is for a different check.');
        }
        if (!cancelled) setCard(data.card);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load this result.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [publicId, quizSlug]);

  useEffect(() => {
    if (!card) return;
    track('Rate Result View', {
      host: apex,
      quiz: quizSlug,
      public_id: publicId,
      type: card.type_key,
    });
  }, [apex, card, publicId, quizSlug]);

  const onShare = async () => {
    track('Rate Share Tapped', { host: apex, quiz: quizSlug, public_id: publicId });
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareLabel('Link copied');
      window.setTimeout(() => setShareLabel('Share your result'), 2500);
    } catch {
      setShareLabel('Copy the link from the address bar');
    }
    try {
      if (navigator.share) {
        await navigator.share({
          title: card?.og_title || configName,
          text: card?.identity_line || 'See the card.',
          url: shareUrl,
        });
      }
    } catch {
      // canceled
    }
  };

  const ogAbs = ogImage.startsWith('http') ? ogImage : `https://${apex}${ogImage}`;

  return (
    <RateShell
      apex={apex}
      name={configName}
      title={card?.og_title || title}
      description={card?.og_description || description}
      path={`/r/${publicId}`}
      harborHome={harborHome}
      cross={cross}
      ogImage={ogAbs}
      noindex
    >
      {error ? (
        <p className='text-center text-gray-600' style={nunito}>
          {error}{' '}
          <RateLink to='/' className='font-semibold text-primary'>
            Keep voting
          </RateLink>
        </p>
      ) : null}
      {card ? (
        <>
          <CheckCard card={card} />
          <CheckHistogram card={card} />
          <div className='mt-8 grid gap-3'>
            <button
              type='button'
              onClick={() => void onShare()}
              className='rounded-2xl bg-primary px-6 py-4 text-lg font-semibold text-white shadow-sm hover:bg-primary-hover'
              style={nunito}
            >
              {shareLabel}
            </button>
            <RateLink
              to='/drop'
              className='rounded-2xl bg-gray-100 px-6 py-4 text-center text-lg font-semibold text-gray-800'
              style={nunito}
            >
              Drop a text
            </RateLink>
            <RateLink
              to='/'
              onClick={() => track('Rate Yours Tapped', { host: apex, quiz: quizSlug })}
              className='rounded-2xl border border-primary px-6 py-4 text-center text-lg font-semibold text-primary'
            >
              Keep voting
            </RateLink>
            <a
              href={harborTool}
              className='pt-2 text-center text-sm font-semibold text-gray-600 underline-offset-2 hover:underline'
              style={nunito}
            >
              {card.cta_label || harborCta}
            </a>
          </div>
          <p className='mt-8 text-center text-xs text-gray-500' style={nunito}>
            Educational, not legal advice. No names stored.
          </p>
        </>
      ) : !error ? (
        <p className='text-center text-gray-500' style={nunito}>
          Loading your card…
        </p>
      ) : null}
    </RateShell>
  );
}

function RateDrop({
  apex,
  configName,
  title,
  description,
  harborHome,
  cross,
}: {
  apex: string;
  configName: string;
  title: string;
  description: string;
  harborHome: string;
  cross?: { href: string; label: string };
}) {
  const isOther = apex === 'ratemycoparent.com';
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('texts');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async () => {
    setBusy(true);
    setError(null);
    track('Rate Submit Tapped', { host: apex, category });
    try {
      const result = await submitScenario(apex, { body, category });
      track('Rate Submit Completed', { host: apex, status: result.status, id: result.id });
      go(`/s/${result.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not hold that text.');
      setBusy(false);
    }
  };

  return (
    <RateShell
      apex={apex}
      name={configName}
      title={title}
      description={description}
      path='/drop'
      harborHome={harborHome}
      cross={cross}
      noindex
    >
      <p className='text-xs font-semibold uppercase tracking-wide text-primary' style={nunito}>
        Drop a text
      </p>
      <h1 className='mt-2 text-3xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
        {isOther ? 'A text they sent' : 'How you handled it'}
      </h1>
      <p className='mt-2 text-gray-600' style={nunito}>
        Situations, not people. No names, no kid names, no photos.
      </p>
      <label className='mt-6 block text-sm font-semibold text-gray-700' style={nunito}>
        Category
        <select
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          className='mt-2 w-full rounded-2xl bg-gray-100 px-4 py-3 text-base font-semibold text-gray-800'
          style={nunito}
        >
          {DROP_CATEGORIES.map((item) => (
            <option key={item.key} value={item.key}>
              {item.label}
            </option>
          ))}
        </select>
      </label>
      <textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        rows={5}
        maxLength={400}
        placeholder='Paste the situation. Strip names first.'
        className='mt-4 w-full rounded-2xl bg-gray-100 px-4 py-3 text-base text-gray-800'
        style={nunito}
      />
      <p className='mt-1 text-right text-xs text-gray-400'>{body.trim().length} / 400</p>
      {error ? (
        <p className='mt-3 text-sm text-red-600' style={nunito}>
          {error}
        </p>
      ) : null}
      <button
        type='button'
        disabled={busy}
        onClick={() => void onSubmit()}
        className='mt-4 w-full rounded-2xl bg-primary px-6 py-4 text-lg font-semibold text-white disabled:opacity-60'
        style={nunito}
      >
        {busy ? 'Holding…' : 'Submit for review'}
      </button>
      <RateLink to='/' className='mt-4 block text-center text-sm font-semibold text-primary'>
        Back to voting
      </RateLink>
    </RateShell>
  );
}

function RateStats({
  apex,
  configName,
  title,
  description,
  harborHome,
  cross,
  submissionId,
}: {
  apex: string;
  configName: string;
  title: string;
  description: string;
  harborHome: string;
  submissionId: string;
  cross?: { href: string; label: string };
}) {
  const [stats, setStats] = useState<RateSubmissionStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchSubmission(apex, submissionId);
        if (!cancelled) setStats(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load that drop.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apex, submissionId]);

  return (
    <RateShell
      apex={apex}
      name={configName}
      title={title}
      description={description}
      path={`/s/${submissionId}`}
      harborHome={harborHome}
      cross={cross}
      noindex
    >
      {error ? (
        <p className='text-gray-600' style={nunito}>
          {error}
        </p>
      ) : null}
      {stats ? (
        <>
          <p className='text-xs font-semibold uppercase tracking-wide text-primary' style={nunito}>
            Your drop
          </p>
          <h1 className='mt-2 text-3xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            {stats.status === 'approved'
              ? `${stats.shown} parents voted`
              : stats.status === 'rejected'
                ? "Couldn't use this one. Too identifying."
                : 'Held for review'}
          </h1>
          <p className='mt-4 rounded-2xl bg-gray-100 px-4 py-4 text-lg font-semibold text-gray-800' style={nunito}>
            {stats.body}
          </p>
          {stats.status === 'approved' ? (
            <p className='mt-4 text-gray-600' style={nunito}>
              {stats.win_pct}% said this one was worse / the one they picked.
            </p>
          ) : (
            <p className='mt-4 text-gray-600' style={nunito}>
              If it is anonymous enough, it enters the vote pool. We never publish names.
            </p>
          )}
          <RateLink
            to='/'
            className='mt-6 block rounded-2xl bg-primary px-6 py-4 text-center text-lg font-semibold text-white'
            style={nunito}
          >
            Keep voting
          </RateLink>
        </>
      ) : !error ? (
        <p className='text-gray-500' style={nunito}>
          Loading…
        </p>
      ) : null}
    </RateShell>
  );
}
